import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { PersonnelDocumentTypeModal } from '../components/PersonnelDocumentTypeModal';
import { personnelDocumentTypesApi } from '../personnelDocumentTypesApi';
import type { PersonnelDocumentType } from '../personnelDocumentTypes';

/**
 * The document checklist — what a 201 file is expected to hold.
 *
 * The anchor opens the **profile**, not the edit form: every index page in this product is
 * partnered with one, and here the profile is where the count of employees who have filed a
 * document of this kind is worth reading before deciding anything about the row.
 */
export function PersonnelDocumentTypesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<PersonnelDocumentType | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    type: PersonnelDocumentType;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchTypes = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      personnelDocumentTypesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (type: PersonnelDocumentType): MenuItem[] => [
      {
        key: `edit-${type.id}`,
        label: 'Edit',
        permission: 'personnel-document-types.update',
        onClick: () => setEditing(type),
      },
      {
        key: `restore-${type.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'personnel-document-types.delete',
        disabledReason: type.isActive ? 'This document is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', type }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await personnelDocumentTypesApi.deactivate(confirming.type.id);
      } else {
        await personnelDocumentTypesApi.activate(confirming.type.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PersonnelDocumentType>
        module="personnel-document-types"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Documents',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['PersonnelDocumentType']}
        rowKey={(type) => type.id}
        fetch={fetchTypes}
        reloadKey={reloadKey}
        searchPlaceholder="Search documents"
        emptyMessage="Nothing on the checklist yet. Add what a 201 file is expected to hold — birth certificate, diploma, appointments."
        onCreate={() => setCreating(true)}
        createPermission="personnel-document-types.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(type) => navigate(`/personnel/settings/documents/${type.id}`)}
        openPermission="personnel-document-types.read"
        rowActions={rowActions}
        onDelete={(type) => setConfirming({ kind: 'deactivate', type })}
        deletePermission="personnel-document-types.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(type) => (type.isActive ? null : 'This document is already deleted.')}
        onClearData={() => personnelDocumentTypesApi.clear()}
        clearDataPermission="personnel-document-types.clear-data"
        moduleNoun="documents"
        otherInfo={(type) => type}
      />

      {creating || editing !== null ? (
        <PersonnelDocumentTypeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete document' : 'Restore document'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`personnel-document-type-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.type.name}" stops being asked for on every 201 file.`
                  : `"${confirming.type.name}" is asked for again on every 201 file.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.type.filedByCount > 0 ? (
                <p>
                  {confirming.type.filedByCount} employee
                  {confirming.type.filedByCount === 1 ? ' has' : 's have'} already filed a
                  document of this kind. Those documents stay on their records and still name
                  it — taking a row off the checklist does not remove what was filed.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted document can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PersonnelDocumentType>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 300,
    flex: true,
    sortable: true,
    render: (type) => <TextCell value={type.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 360,
    summary: true,
    render: (type) => <TextCell value={type.description} />,
  },
  {
    // The stored sort value, under a heading that says so: a POSITION is only correct with the
    // whole ladder in hand, and a paged table never has it.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (type) => <TextCell value={String(type.sortOrder)} mono numeric />,
  },
  {
    key: 'filedByCount',
    header: 'Employees',
    width: 110,
    align: 'right',
    summary: true,
    // The count is of personnel records, which is another module's data — the name and
    // description beside it are this module's own and stay ungated.
    permission: 'personnel.read',
    render: (type) => <TextCell value={String(type.filedByCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (type) =>
      type.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
